import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './css/PasswordGenerator.css';

const CHARSETS = {
  upper: { label: 'Uppercase', sample: 'A-Z', chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
  lower: { label: 'Lowercase', sample: 'a-z', chars: 'abcdefghijklmnopqrstuvwxyz' },
  numbers: { label: 'Numbers', sample: '0-9', chars: '0123456789' },
  symbols: { label: 'Symbols', sample: '!@#$', chars: '!@#$%&?*-_+=' },
};

const MIN_LENGTH = 5;
const MAX_LENGTH = 64;

// Unbiased random integer in [0, max) using the Web Crypto API
const randomInt = (max) => {
  const limit = Math.floor(0x100000000 / max) * max;
  const buf = new Uint32Array(1);
  do {
    crypto.getRandomValues(buf);
  } while (buf[0] >= limit);
  return buf[0] % max;
};

const generatePassword = (length, options) => {
  const sets = Object.keys(CHARSETS)
    .filter((key) => options[key])
    .map((key) => CHARSETS[key].chars);
  if (sets.length === 0) return '';

  // Guarantee at least one character from every selected set
  const chars = sets.map((set) => set[randomInt(set.length)]);
  const pool = sets.join('');
  while (chars.length < length) chars.push(pool[randomInt(pool.length)]);

  // Fisher-Yates shuffle so the guaranteed characters aren't always first
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

const getStrength = (length, options) => {
  const poolSize = Object.keys(CHARSETS)
    .filter((key) => options[key])
    .reduce((sum, key) => sum + CHARSETS[key].chars.length, 0);
  const entropy = poolSize ? Math.round(length * Math.log2(poolSize)) : 0;

  if (entropy >= 100) return { entropy, level: 4, label: 'Very strong' };
  if (entropy >= 70) return { entropy, level: 3, label: 'Strong' };
  if (entropy >= 45) return { entropy, level: 2, label: 'Medium' };
  return { entropy, level: 1, label: 'Weak' };
};

const PasswordGenerator = () => {
  const [length, setLength] = useState(16);
  const [options, setOptions] = useState({ upper: true, lower: true, numbers: true, symbols: true });
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const regenerate = useCallback(() => {
    setPassword(generatePassword(length, options));
    setCopied(false);
  }, [length, options]);

  useEffect(() => {
    regenerate();
  }, [regenerate]);

  const toggleOption = (key) => {
    const enabledCount = Object.values(options).filter(Boolean).length;
    // Keep at least one charset enabled
    if (options[key] && enabledCount === 1) return;
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLengthInput = (value) => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return;
    setLength(Math.min(MAX_LENGTH, Math.max(MIN_LENGTH, n)));
  };

  const copyPassword = async () => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  const strength = getStrength(length, options);
  const fillPercent = ((length - MIN_LENGTH) / (MAX_LENGTH - MIN_LENGTH)) * 100;

  return (
    <section className="pwgen-section">
      <Link to="/projects" className="pwgen-back">
        ← Projects
      </Link>

      <header className="pwgen-header">
        <span className="pwgen-tag">// tool</span>
        <h1 className="pwgen-title">Password Generator</h1>
        <p className="pwgen-subtitle">
          Cryptographically secure passwords, generated locally in your browser. Nothing leaves this page.
        </p>
      </header>

      <div className="pwgen-panel">
        <div className="pwgen-terminal">
          <div className="pwgen-terminal-bar">
            <span className="dot red" />
            <span className="dot yellow" />
            <span className="dot green" />
            <span className="pwgen-terminal-name">fn@studios:~/pwgen</span>
          </div>
          <div className="pwgen-output">
            <span className="pwgen-prompt">$</span>
            <span className="pwgen-password" key={password}>
              {password}
            </span>
            <span className="pwgen-cursor" />
          </div>
          <div className="pwgen-actions">
            <button type="button" className="pwgen-btn" onClick={regenerate}>
              <i className="fa-solid fa-rotate" /> Regenerate
            </button>
            <button type="button" className={`pwgen-btn primary ${copied ? 'copied' : ''}`} onClick={copyPassword}>
              <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} /> {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="pwgen-strength">
          <div className="pwgen-strength-bars">
            {[1, 2, 3, 4].map((i) => (
              <span key={i} className={`bar ${i <= strength.level ? `on level-${strength.level}` : ''}`} />
            ))}
          </div>
          <span className={`pwgen-strength-label level-${strength.level}`}>
            {strength.label} · ~{strength.entropy} bits
          </span>
        </div>

        <div className="pwgen-controls">
          <div className="pwgen-length">
            <div className="pwgen-length-head">
              <label htmlFor="pwgen-range">Length</label>
              <input
                type="number"
                className="pwgen-length-number"
                min={MIN_LENGTH}
                max={MAX_LENGTH}
                value={length}
                onChange={(e) => handleLengthInput(e.target.value)}
              />
            </div>
            <input
              id="pwgen-range"
              type="range"
              className="pwgen-range"
              min={MIN_LENGTH}
              max={MAX_LENGTH}
              value={length}
              style={{ '--fill': `${fillPercent}%` }}
              onChange={(e) => handleLengthInput(e.target.value)}
            />
          </div>

          <div className="pwgen-options">
            {Object.entries(CHARSETS).map(([key, { label, sample }]) => (
              <button
                type="button"
                key={key}
                className={`pwgen-option ${options[key] ? 'active' : ''}`}
                onClick={() => toggleOption(key)}
                aria-pressed={options[key]}
              >
                <span className="pwgen-switch" />
                <span className="pwgen-option-label">{label}</span>
                <span className="pwgen-option-sample">{sample}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <footer className="pwgen-footer">
        <p>© 2025 FN Studios. All rights reserved.</p>
      </footer>
    </section>
  );
};

export default PasswordGenerator;
